// Unified logger for b2b-portal that works on both client and server
import { logger as serverLogger } from "@scott/logger";

// Browser logger interface for consistency
type BrowserLoggerInterface = {
  info: (message: string, meta?: object) => void;
  error: (message: string, error?: Error, meta?: object) => void;
  warn: (message: string, meta?: object) => void;
  debug: (message: string, meta?: object) => void;
  performance: (operation: string, duration: number, meta?: object) => void;
  userAction: (action: string, userId?: string, meta?: object) => void;
  businessMetric: (
    metric: string,
    value: number,
    tags?: Record<string, string>
  ) => void;
  rum: {
    addAction: (name: string, context?: object) => void;
    addError: (error: Error | string, context?: object) => void;
    addTiming: (name: string, time?: number) => void;
    setUser: (user: { id?: string; name?: string; email?: string }) => void;
    setGlobalContextProperty: (key: string, value: unknown) => void;
  };
};

// Cache for browser logger to avoid multiple imports
let browserLoggerCache: BrowserLoggerInterface | null = null;
let browserLoggerPromise: Promise<BrowserLoggerInterface | null> | null = null;

// Get browser logger with caching
const getBrowserLogger = (): Promise<BrowserLoggerInterface | null> => {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (browserLoggerCache) {
    return Promise.resolve(browserLoggerCache);
  }

  if (browserLoggerPromise) {
    return browserLoggerPromise;
  }

  browserLoggerPromise = import("../services/datadog/datadog-browser").then(
    (module) => {
      browserLoggerCache = module.browserLogger;
      return browserLoggerCache;
    }
  );
  return browserLoggerPromise;
};

// Unified logger that works on both client and server
export const logger = {
  info: (message: string, meta?: object) => {
    if (typeof window === "undefined") {
      // Server-side
      serverLogger.info(message, meta);
    } else {
      // Client-side - async but fire-and-forget
      void getBrowserLogger().then((bl) => bl?.info(message, meta));
    }
  },

  error: async (message: string, error?: Error, meta?: object) => {
    try {
      if (typeof window === "undefined") {
        serverLogger.error(message, error, meta);
      } else {
        const browserLogger = await getBrowserLogger();
        browserLogger?.error(message, error, meta);
      }
    } catch {
      // Logging must never break error UI or recovery.
    }
  },

  warn: (message: string, meta?: object) => {
    if (typeof window === "undefined") {
      // Server-side
      serverLogger.warn(message, meta);
    } else {
      // Client-side - async but fire-and-forget
      void getBrowserLogger().then((bl) => bl?.warn(message, meta));
    }
  },

  debug: (message: string, meta?: object) => {
    if (typeof window === "undefined") {
      // Server-side
      serverLogger.debug(message, meta);
    } else {
      // Client-side - async but fire-and-forget
      void getBrowserLogger().then((bl) => bl?.debug(message, meta));
    }
  },

  performance: (operation: string, duration: number, meta?: object) => {
    if (typeof window === "undefined") {
      // Server-side
      serverLogger.info(`Performance: ${operation}`, { duration, ...meta });
    } else {
      // Client-side - async but fire-and-forget
      void getBrowserLogger().then((bl) =>
        bl?.performance(operation, duration, meta)
      );
    }
  },

  userAction: (action: string, userId?: string, meta?: object) => {
    if (typeof window === "undefined") {
      // Server-side
      serverLogger.info(`User Action: ${action}`, { userId, ...meta });
    } else {
      // Client-side - async but fire-and-forget
      void getBrowserLogger().then((bl) =>
        bl?.userAction(action, userId, meta)
      );
    }
  },

  businessMetric: (
    metric: string,
    value: number,
    tags?: Record<string, string>
  ) => {
    if (typeof window === "undefined") {
      // Server-side
      serverLogger.info(`Business Metric: ${metric}`, { value, ...tags });
    } else {
      // Client-side - async but fire-and-forget
      void getBrowserLogger().then((bl) =>
        bl?.businessMetric(metric, value, tags)
      );
    }
  },

  // Browser-specific RUM methods (only available on client)
  rum: {
    addAction: (name: string, context?: object) => {
      if (typeof window !== "undefined") {
        void getBrowserLogger().then((bl) => bl?.rum?.addAction(name, context));
      }
    },

    addError: (error: Error | string, context?: object) => {
      if (typeof window !== "undefined") {
        void getBrowserLogger().then((bl) => bl?.rum?.addError(error, context));
      }
    },

    addTiming: (name: string, time?: number) => {
      if (typeof window !== "undefined") {
        void getBrowserLogger().then((bl) => bl?.rum?.addTiming(name, time));
      }
    },

    setUser: (user: { id?: string; name?: string; email?: string }) => {
      if (typeof window !== "undefined") {
        void getBrowserLogger().then((bl) => bl?.rum?.setUser(user));
      }
    },

    setGlobalContextProperty: (key: string, value: unknown) => {
      if (typeof window !== "undefined") {
        void getBrowserLogger().then((bl) =>
          bl?.rum?.setGlobalContextProperty(key, value)
        );
      }
    },
  },
};
